import type { Agent } from './types'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export const fullName = (a: Pick<Agent, 'first_name' | 'last_name'>) =>
  [a.first_name, a.last_name].filter(Boolean).join(' ') || 'Unnamed agent'

export const initials = (a: Pick<Agent, 'first_name' | 'last_name'>) =>
  [a.first_name?.[0], a.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?'

/**
 * Sparse dates are not missing data. Metabase only exposes birthdays and
 * anniversaries for agents whose date falls inside its rolling windows, so
 * coverage accumulates across pulls. A null here means "we have not seen it
 * yet", never "this person has no birthday" — and the UI has to say so, or
 * someone will eventually read a blank cell as a fact.
 */
export const NOT_KNOWN_YET = 'Not known yet'

export function monthDay(month: number | null, day: number | null): string | null {
  if (month == null || day == null) return null
  const name = MONTHS[month - 1]
  if (!name) return null
  return `${name} ${day}`
}

/** Day-of-year distance from today, wrapping past the end of the year. */
export function daysUntil(month: number | null, day: number | null): number | null {
  if (month == null || day == null) return null
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let next = new Date(now.getFullYear(), month - 1, day)
  if (next < today) next = new Date(now.getFullYear() + 1, month - 1, day)
  return Math.round((next.getTime() - today.getTime()) / 86_400_000)
}

export function relativeDay(days: number | null): string | null {
  if (days == null) return null
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days <= 30) return `in ${days} days`
  return null
}

/**
 * Years with Real, which is why `anniversary` exists as a full date at all.
 * Only 50 of 167 anniversaries carry a year — the rest are month/day only and
 * correctly return null here rather than guessing.
 */
export function yearsWithReal(agent: Agent): number | null {
  if (!agent.anniversary_year) return null
  const now = new Date()
  const hadItThisYear =
    agent.anniversary_month != null &&
    agent.anniversary_day != null &&
    (now.getMonth() + 1 > agent.anniversary_month ||
      (now.getMonth() + 1 === agent.anniversary_month &&
        now.getDate() >= agent.anniversary_day))
  const years = now.getFullYear() - agent.anniversary_year
  return hadItThisYear ? years : years - 1
}

/**
 * A human duration between two dates — the two comparisons the old
 * lifecycle-email skill this project was seeded from made routine: how long
 * from joining to cap, and cap to Elite. Whole months only; nobody needs "61
 * days," they need "2 months."
 *
 * Read in UTC calendar fields on both ends, on purpose — the same trap
 * `formatDateLong` guards against, but a difference between two dates only
 * needs the two ends read the SAME way, not the locally correct way, so
 * there is no separate date-only-vs-timestamp branch here.
 *
 * Null for anything that is not a real duration: either date missing or
 * unreadable, or the end not after the start — a data ordering problem
 * (Metabase disagreeing with itself, a correction), not a span to report.
 */
export function monthsBetween(startValue: string | null, endValue: string | null): string | null {
  if (!startValue || !endValue) return null
  const start = new Date(startValue)
  const end = new Date(endValue)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  if (end.getTime() <= start.getTime()) return null
  let months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth())
  if (end.getUTCDate() < start.getUTCDate()) months -= 1
  if (months < 1) return 'less than a month'
  const years = Math.floor(months / 12)
  const remainder = months % 12
  if (years === 0) return `${months} month${months === 1 ? '' : 's'}`
  const yearPart = `${years} year${years === 1 ? '' : 's'}`
  return remainder === 0 ? yearPart : `${yearPart}, ${remainder} month${remainder === 1 ? '' : 's'}`
}

/**
 * Whole weeks remaining until a future date, from today — the forward
 * complement to `monthsBetween`'s backward-looking spans. Added 2026-09-11,
 * for the "Cap reached" template's time-to-reset line: Chris's own draft to
 * an agent read "eight weeks" in one place and "two months" in another —
 * two different hand-rounded guesses at the same span, typed by hand
 * because nothing computed it. `cap_reset_date` already has its own
 * reliable daily refresh (`planMilestoneDates`/`isNewCycle`), so this needed
 * no new data, only the arithmetic, run forward instead of back.
 *
 * A companion "closings this cap cycle" field was asked for the same day
 * and ruled out — Metabase's own Closed Transactions figure is a lifetime
 * total, and a true cap-cycle count would mean reading every one of an
 * agent's closed transactions out of reZEN directly, which isn't a
 * realistic build. See
 * `claude/cap-reached-template-merge-fields-2026-09-11.md`.
 *
 * Rounds to the nearest week rather than flooring, so 55 days out reads as
 * "8 weeks" rather than an oddly precise "7". Never renders as zero for a
 * date that has not arrived yet — one a day or two out rounds up to "1
 * week" rather than the nonsensical "resets in 0 weeks." Null for a date
 * that is missing, unparseable, or already today or past — nobody gets a
 * countdown to something that has already happened.
 *
 * `now` is a parameter, not a bare `new Date()` inside the function, so a
 * test can pin it — the same reason `yearsWithReal`'s callers pin the
 * anniversary rather than trying to freeze the clock.
 */
export function weeksUntil(targetValue: string | null, now: Date = new Date()): number | null {
  if (!targetValue) return null
  const target = new Date(targetValue)
  if (Number.isNaN(target.getTime())) return null
  // "Today" read the same way `localDate` reads it — local calendar fields,
  // not a UTC snapshot that can already be tomorrow east of Greenwich after
  // dinner — then parsed the same way any other date-only column is parsed,
  // so both ends of the subtraction are midnight UTC of a calendar day and
  // the difference is a clean day count. Built locally rather than imported
  // from `campaigns.ts`, which imports this file, to avoid a circular import.
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  const today = new Date(`${y}-${m}-${d}`)
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000)
  if (diffDays <= 0) return null
  return Math.max(1, Math.round(diffDays / 7))
}

/**
 * A bare `2027-06-30` parses as midnight UTC, and read back in New York that is
 * the evening of the 29th. Every date-only column in this database — licence
 * expiry, cap and elite dates, cap reset — was therefore being shown a day
 * early, all day, every day west of Greenwich.
 *
 * Nobody noticed until the licence-expiry filter put the correct date in a
 * dropdown next to the wrong one on the detail panel. Full timestamps keep the
 * local zone, which is right for them: `first_transaction_closed_at` is a
 * moment, not a calendar day.
 */
export function formatDate(value: string | null): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' as const } : {}),
  })
}

/**
 * The same date, spelled out, for text a person reads in an email.
 *
 * "Feb 1, 2027" is right for a dense panel of rows and wrong in the middle of a
 * sentence Renee signed. This is the one that goes into merge fields.
 *
 * The `timeZone: 'UTC'` on a date-only string is not decoration and is the
 * reason this shares `formatDate`'s shape rather than being written fresh:
 * `new Date('2027-02-01')` is midnight UTC, which is 7pm on 31 January in
 * Eastern time. Rendered locally, every cap reset date in the roster would
 * print as the last day of the previous month.
 */
export function formatDateLong(value: string | null): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' as const } : {}),
  })
}

export function formatDateTime(value: string | null): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export const TRIGGER_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  birthday: 'Birthday',
  anniversary: 'Work anniversary',
  capped: 'Cap reached',
  elite: 'Elite reached',
  first_closing: 'First closing',
  firstclosing: 'First closing',
}

/**
 * What a trigger is called on screen.
 *
 * Drip triggers carry the campaign's id — `drip:8f3c9e21-4b7a-…` — and printing
 * that raw put a UUID in the agent's history beside rows reading "Birthday".
 * The agent panel is where Renee answers "what have we sent this person", so it
 * is the last place for an identifier only a database would recognise.
 *
 * The campaign's *name* is not available to the components that render this, so
 * this says what kind of thing it was rather than inventing a lookup. Better a
 * true general answer than a precise one that needs a fifth query.
 */
export const triggerLabel = (t: string) =>
  TRIGGER_LABELS[t] ?? (t.startsWith('drip:') ? 'Drip campaign' : t)
